/**
 * Retailers — LPM first-level dining and entertainment.
 *
 * Matches https://www.lpmapartments.com/floor-plans/retailers:
 * left-ruled "First Level Retailers" intro, then a 50/50 featured
 * tenant (copy + address + VIEW WEBSITE | photo). Image stacks above
 * copy on mobile, matching the live `mobile-columns-reversed` row.
 */

import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useEditMode,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import bruhavenPhoto from '../assets/retailers/bruhaven.jpg';

const BRUHAVEN_PHOTO_URL =
  'https://irp.cdn-website.com/696d2a94/dms3rep/multi/opt/Bruhaven+LPM+apartments-1920w.jpg';
const BRUHAVEN_PHOTO_ALT = 'brühaven brewery at LPM Apartments';

const featuredPhotoDef: SlotGroupDefinition = {
  label: 'Featured Photo',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/696d2a94/dms3rep/multi/opt/Bruhaven+LPM+apartments-1920w.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'brühaven brewery at LPM Apartments',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 24,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A photo of the featured on-site first-level retailer, restaurant, or brewery that actually operates at this property. Prefer a storefront, dining room, or taproom shot. Do not invent a tenant.',
    context:
      'Large photo on the right of the featured-retailer split on the Retailers page.',
  },
};

const addressDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    url: {
      label: 'Map URL',
      type: 'url',
      defaultValue: 'https://goo.gl/maps/voLKwJpGfcmiGKGeA',
    },
    label: {
      label: 'Address',
      type: 'text',
      defaultValue: '1368 Lasalle Ave, Minneapolis, MN 55403',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'Website CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://bruhaven.com/',
    },
    label: {
      label: 'Button Label',
      type: 'text',
      defaultValue: 'View Website',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

export function Retailers() {
  const headline = useContentToken('retailers.headline');
  const name = useContentToken('retailers.name');
  const photo = useSlotGroup('retailers.photo', featuredPhotoDef);
  const address = useSlotGroup('retailers.address', addressDef);
  const cta = useSlotGroup('retailers.cta', ctaDef);
  const editMode = useEditMode();
  const isEditing = !!editMode?.isEditMode;
  const sectionRef = useScrollReveal<HTMLElement>();

  const src =
    !photo.image || photo.image === BRUHAVEN_PHOTO_URL ? bruhavenPhoto : photo.image;
  const displayName = (name || '').toString().trim();
  const addressLabel = (address.label || '').toString().trim();
  const showAddress = !!(addressLabel || isEditing);
  const showCta = !!(cta.url || isEditing);

  return (
    <section ref={sectionRef} className="retailers">
      <div className="retailers__intro">
        <div className="retailers__heading-block">
          <EditableToken path="retailers.headline" as="h2" className="retailers__headline">
            {headline || 'First Level Retailers'}
          </EditableToken>
        </div>
        <RichTextContent path="retailers.body" label="Intro" as="div" className="retailers__body" />
      </div>

      <div className="retailers__feature">
        <div className="retailers__copy">
          <EditableToken path="retailers.name" as="h3" className="retailers__name">
            {displayName || 'brühaven brewery'}
          </EditableToken>
          <RichTextContent
            path="retailers.blurb"
            label="Description"
            as="div"
            className="retailers__blurb"
          />
          {showAddress ? (
            <EditableSlotGroup
              groupPath="retailers.address"
              definition={addressDef}
              className="retailers__address-wrap"
            >
              {address.url ? (
                <a
                  href={address.url}
                  target={address._target || '_blank'}
                  rel={address._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="retailers__address"
                >
                  {addressLabel || 'Add address'}
                </a>
              ) : (
                <span className="retailers__address">{addressLabel || 'Add address'}</span>
              )}
            </EditableSlotGroup>
          ) : null}
          {showCta ? (
            <EditableSlotGroup
              groupPath="retailers.cta"
              definition={ctaDef}
              className="retailers__cta-wrap"
            >
              <a
                href={cta.url || 'https://bruhaven.com/'}
                target={cta._target || '_blank'}
                rel={cta._target === '_blank' ? 'noopener noreferrer' : undefined}
                className="retailers__cta"
              >
                {cta.label || 'View Website'}
              </a>
            </EditableSlotGroup>
          ) : null}
        </div>

        <EditableSlotGroup
          groupPath="retailers.photo"
          definition={featuredPhotoDef}
          className="retailers__media"
        >
          {src ? (
            <VibesitesImage
              url={src}
              alt={photo.alt || BRUHAVEN_PHOTO_ALT}
              sizes="(min-width: 992px) 50vw, 100vw"
              className="retailers__img"
            />
          ) : isEditing ? (
            <div className="retailers__placeholder">Add retailer photo</div>
          ) : null}
        </EditableSlotGroup>
      </div>

      <style>{`
        .retailers {
          background: #fff;
          padding: 3.25rem 1.25rem 4.5rem;
        }
        @media (min-width: 768px) {
          .retailers { padding: 3.25rem 2rem 5.5rem; }
        }
        @media (min-width: 1200px) {
          .retailers { padding: 50px 40px 6.5rem; }
        }

        .retailers__intro {
          max-width: 1200px;
          margin: 0 auto 2.75rem;
        }

        .retailers__heading-block {
          border-left: 10px solid #cbc4bc;
          padding: 0 0 0 1.25rem;
          margin: 0 0 1.5rem;
        }

        .retailers__headline {
          margin: 0;
          font-family: var(--vibe-font-heading);
          font-size: 3rem;
          font-weight: 700;
          letter-spacing: 0;
          line-height: 1;
          color: var(--vibe-color-primary);
        }
        @media (min-width: 768px) {
          .retailers__headline { font-size: 4rem; }
        }
        @media (min-width: 1200px) {
          .retailers__headline { font-size: 5rem; }
        }

        .retailers__body {
          max-width: 52rem;
          font-family: var(--vibe-font-body);
          font-size: 15px;
          font-weight: 400;
          line-height: 2;
          color: #000;
        }
        .retailers__body p { margin: 0; }
        .retailers__body a {
          color: var(--vibe-color-primary);
          font-weight: 700;
          text-decoration: none;
        }
        .retailers__body a:hover { text-decoration: underline; }

        .retailers__feature {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-areas:
            "photo"
            "copy";
          gap: 0;
        }
        @media (min-width: 992px) {
          .retailers__feature {
            grid-template-areas: "copy photo";
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            align-items: stretch;
          }
        }

        .retailers__copy {
          grid-area: copy;
          display: flex;
          flex-direction: column;
          justify-content: center;
          min-width: 0;
          padding: 2rem 0 0;
        }
        @media (min-width: 992px) {
          .retailers__copy { padding: 4.4rem 1.25rem 4.4rem 0; }
        }

        .retailers__name {
          margin: 0 0 1.25rem;
          font-family: var(--vibe-font-heading);
          font-size: 3rem;
          font-weight: 700;
          letter-spacing: 0;
          line-height: 1;
          text-transform: uppercase;
          color: #cbc4bc;
        }
        @media (min-width: 1200px) {
          .retailers__name { font-size: 3.75rem; }
        }

        .retailers__blurb {
          font-family: var(--vibe-font-body);
          font-size: 15px;
          line-height: 2;
          color: #000;
        }
        .retailers__blurb p { margin: 0; }

        .retailers__address-wrap {
          display: block;
          width: fit-content;
          margin: 1.25rem 0 0.5rem;
        }

        .retailers__address {
          font-family: var(--vibe-font-body);
          font-size: 15px;
          font-weight: 700;
          line-height: 1.6;
          color: var(--vibe-color-primary);
          text-decoration: none;
        }
        a.retailers__address:hover { text-decoration: underline; }

        .retailers__cta-wrap {
          display: block;
          width: fit-content;
          margin: 1.75rem 0 0;
          transform-origin: center center;
        }

        .retailers__cta {
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          min-width: 18rem;
          height: 60px;
          padding: 0 1.5rem;
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 0.125em;
          text-transform: uppercase;
          text-decoration: none;
          border: none;
          border-radius: 0;
          transition: background 0.25s ease, color 0.25s ease;
        }
        .retailers__cta:hover {
          background: #b6b6b6;
          color: #000;
        }

        .retailers__media {
          grid-area: photo;
          display: block;
          min-width: 0;
          min-height: 16rem;
          background: #ececec;
          overflow: hidden;
        }
        @media (min-width: 992px) {
          .retailers__media { min-height: 42.75rem; }
        }

        .retailers__img {
          display: block;
          width: 100%;
          height: 100%;
          min-height: 16rem;
          object-fit: cover;
          object-position: center;
        }
        @media (min-width: 992px) {
          .retailers__img { min-height: 42.75rem; }
        }

        .retailers__placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 16rem;
          height: 100%;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
        }

        .retailers__heading-block,
        .retailers__body,
        .retailers__name,
        .retailers__blurb,
        .retailers__address-wrap,
        .retailers__cta-wrap {
          transition: opacity 1.2s cubic-bezier(0.22, 1, 0.36, 1),
                      transform 1.2s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .retailers.reveal--visible .retailers__body { transition-delay: 0.15s; }
        .retailers.reveal--visible .retailers__name { transition-delay: 0.1s; }
        .retailers.reveal--visible .retailers__blurb { transition-delay: 0.22s; }
        .retailers.reveal--visible .retailers__address-wrap { transition-delay: 0.3s; }
        .retailers.reveal--visible .retailers__cta-wrap { transition-delay: 0.4s; }

        .retailers.reveal--hidden .retailers__heading-block,
        .retailers.reveal--hidden .retailers__name {
          opacity: 0;
          transform: translateX(-28px);
        }
        .retailers.reveal--hidden .retailers__body,
        .retailers.reveal--hidden .retailers__blurb,
        .retailers.reveal--hidden .retailers__address-wrap {
          opacity: 0;
          transform: translateY(28px);
        }
        .retailers.reveal--hidden .retailers__cta-wrap {
          opacity: 0;
          transform: scale(0.6);
        }
        .retailers.reveal--visible .retailers__heading-block,
        .retailers.reveal--visible .retailers__body,
        .retailers.reveal--visible .retailers__name,
        .retailers.reveal--visible .retailers__blurb,
        .retailers.reveal--visible .retailers__address-wrap,
        .retailers.reveal--visible .retailers__cta-wrap {
          opacity: 1;
          transform: none;
        }

        @media (prefers-reduced-motion: reduce) {
          .retailers__heading-block,
          .retailers__body,
          .retailers__name,
          .retailers__blurb,
          .retailers__address-wrap,
          .retailers__cta-wrap,
          .retailers.reveal--hidden .retailers__heading-block,
          .retailers.reveal--hidden .retailers__body,
          .retailers.reveal--hidden .retailers__name,
          .retailers.reveal--hidden .retailers__blurb,
          .retailers.reveal--hidden .retailers__address-wrap,
          .retailers.reveal--hidden .retailers__cta-wrap {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Retailers',
  displayName: 'Retailers',
  tokens: {
    'retailers.headline': {
      label: 'Headline',
      type: 'text',
      defaultValue: 'First Level Retailers',
    },
    'retailers.body': {
      label: 'Intro',
      type: 'richtext',
      defaultValue:
        '<p>On site, you can enjoy everything from breakfast at Eggy\u2019s Diner or enjoy your favorite brew at <a href="https://bruhaven.com/" target="_blank" rel="noopener noreferrer"><strong>Bruhaven Brewery</strong></a>. Venture to the edgy North Loop area and catch a show at Acme Comedy Company, one of the most popular comedy clubs in the country. Experience 360-degree views of downtown and learn about Minneapolis\u2019 rich history when you visit the Foshay Museum and Observation Deck. On the way back home, pick up groceries at the nearby Lunds &amp; Byerly\u2019s or have dinner at 4 Bells, one of Minneapolis\u2019 award-winning restaurants. If you are looking to stay close to home, spend a day at the Walker Art Museum and adjacent Sculpture Garden or enjoy an evening at the Dakota Jazz Club.</p>',
      generator: {
        prompt:
          'A neighborhood dining and entertainment intro for this property. Name only on-site first-level tenants that actually operate here, then real nearby restaurants, venues, or cultural spots. Link the featured on-site retailer if a real website is known. Do not invent tenants or attractions.',
        context:
          'Left-aligned intro under First Level Retailers on the Retailers page, above the featured tenant split.',
        length: '4–6 sentences',
      },
    },
    'retailers.name': {
      label: 'Featured Name',
      type: 'text',
      defaultValue: 'brühaven brewery',
      generator: {
        prompt:
          'The real name of the on-site first-level retailer, restaurant, or brewery featured on this page. Do not invent a tenant.',
        context: 'Large beige uppercase name on the left of the featured-retailer split.',
      },
    },
    'retailers.blurb': {
      label: 'Description',
      type: 'richtext',
      defaultValue:
        '<p>Preserving a legacy while infusing a new spirit of community and innovation. Brühaven is the go-to spot in Loring Park for craft brews, coffee, and camaraderie. We\u2019re committed to creating an inviting haven where friends, families, and neighbors can connect and make lasting memories\u2014 no matter what you are sippin\'.</p>',
      generator: {
        prompt:
          'Two or three sentences describing the featured on-site retailer in its own voice. Use only facts that are true for this tenant.',
        context: 'Body copy under the featured retailer name, above the address and website button.',
        length: '2–3 sentences',
      },
    },
  },
  slotGroups: {
    'retailers.photo': featuredPhotoDef,
    'retailers.address': addressDef,
    'retailers.cta': ctaDef,
  },
};

(Retailers as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Retailers;
